import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { GridApiPro, GridCsvExportOptions, gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid-pro';
import { ReportTab } from '../../../Api/Report/reportApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { downloadWorkbook, parseCsv, restoreTrailingRows, sheetNames } from '../../../utils/reportWorkbook';

interface ScopedState {
  scope: string;
  counts: (number | null)[];
  activeIndex: number;
}

export const WORKBOOK_CSV_OPTIONS: GridCsvExportOptions = {
  includeHeaders: true,
  escapeFormulas: false,
  getRowsToExport: ({ apiRef }) => gridFilteredSortedRowIdsSelector(apiRef)
};

export interface ReportWorkbook {
  apiRefs: MutableRefObject<GridApiPro>[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  counts: (number | null)[];
  onCount: (index: number, count: number) => void;
  ready: boolean;
  downloading: boolean;
  download: () => Promise<void>;
}

const freshState = (scope: string, tabCount: number): ScopedState => ({
  scope,
  counts: Array(tabCount).fill(null),
  activeIndex: 0
});

/** Reads the workbook from the tabs' mounted grids rather than rebuilding it from the rows, so each sheet carries its tab's own view. */
export const useReportWorkbook = (tabs: ReportTab[], fileName: string, resetKey: string): ReportWorkbook => {
  const tabCount = tabs.length;
  const scope = `${resetKey}|${tabCount}`;
  const [state, setState] = useState<ScopedState>(() => freshState(scope, tabCount));
  if (state.scope !== scope) setState(freshState(scope, tabCount));
  const { setSnack } = useSnackbar();
  const [downloading, setDownloading] = useState(false);
  const apiRefs = useMemo(() => Array.from({ length: tabCount }, () => ({ current: {} as GridApiPro })), [tabCount]);
  const { counts, activeIndex } = state;

  const onCount = useCallback(
    (index: number, count: number) =>
      setState((previous) =>
        previous.scope !== scope || previous.counts[index] === count
          ? previous
          : { ...previous, counts: previous.counts.map((value, i) => (i === index ? count : value)) }
      ),
    [scope]
  );

  const setActiveIndex = useCallback(
    (index: number) =>
      setState((previous) => (previous.scope === scope ? { ...previous, activeIndex: index } : previous)),
    [scope]
  );

  const ready = counts.length === tabCount && counts.every((count) => count !== null);

  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const names = sheetNames(tabs.map((tab) => tab.tab_name));
      const sheets = apiRefs.map((ref, index) => {
        if (!ref.current.getDataAsCsv) throw new Error(`No data could be read for the sheet "${names[index]}"`);
        const csv = ref.current.getDataAsCsv(WORKBOOK_CSV_OPTIONS);
        return { name: names[index], rows: restoreTrailingRows(parseCsv(csv), counts[index] ?? 0) };
      });
      await downloadWorkbook(fileName, sheets);
    } catch (error) {
      datadogLogs.logger.error('Error when creating the report workbook: ', { tabCount }, error as Error);
      setSnack({ open: true, type: 'error', message: 'We could not create the Excel file. Please try again.' });
    } finally {
      setDownloading(false);
    }
  }, [tabs, tabCount, apiRefs, counts, fileName, setSnack]);

  return { apiRefs, activeIndex, setActiveIndex, counts, onCount, ready, downloading, download };
};
